// What you were just looking at, and what you keep coming back to.
//
// Getting back to the thing you had open five minutes ago meant finding it
// again from a list. Every record now has an address (lib/recordLinks), so
// the sidebar can remember the last few you opened and the ones you pin.
// Kept in this browser: it is a convenience for the person at the desk, not
// a record anybody else reads.

import { clientPath, projectPath, invoicePath } from './recordLinks'

const RECENT_KEY = 'ac_recent_records'
const PINNED_KEY = 'ac_pinned_records'
const CHANGED = 'ac-recent-changed'
const MAX_RECENT = 12

/** The record an address points at, or null for a list or any other page. */
export function parseRecord(pathname = '', search = '') {
  const q = new URLSearchParams(search)
  let m
  if (pathname === '/admin' && q.get('client')) return { type: 'client', id: q.get('client') }
  if (pathname === '/projects' && q.get('project')) return { type: 'project', id: q.get('project') }
  if (pathname === '/invoices' && q.get('invoice')) return { type: 'invoice', id: q.get('invoice') }
  if (pathname === '/proposals' && q.get('proposal')) return { type: 'proposal', id: q.get('proposal') }
  if ((m = pathname.match(/^\/proposals\/([^/]+)\/studio$/))) return { type: 'proposal', id: m[1] }
  if ((m = pathname.match(/^\/bids\/([^/]+)(\/studio)?$/))) return { type: 'bid', id: m[1] }
  return null
}

/** Where to send someone back to a record. Proposals reopen in the studio, which works for both kinds. */
export function pathFor(rec) {
  switch (rec?.type) {
    case 'client': return clientPath(rec.id)
    case 'project': return projectPath(rec.id)
    case 'invoice': return invoicePath(rec.id)
    case 'proposal': return rec.studio === false ? `/proposals?proposal=${rec.id}` : `/proposals/${rec.id}/studio`
    case 'bid': return `/bids/${rec.id}`
    default: return '/dashboard'
  }
}

const same = (a, b) => a?.type === b?.type && a?.id === b?.id

function read(key) {
  try { return JSON.parse(localStorage.getItem(key) || '[]') || [] } catch { return [] }
}
function write(key, list) {
  try { localStorage.setItem(key, JSON.stringify(list)) } catch {}
  try { window.dispatchEvent(new Event(CHANGED)) } catch {}
}

export const readRecent = () => read(RECENT_KEY)
export const readPinned = () => read(PINNED_KEY)

/** The newest visit goes first; a record appears once, at its latest visit. */
export function withVisit(list, rec, max = MAX_RECENT) {
  return [rec, ...list.filter(r => !same(r, rec))].slice(0, max)
}

export function recordVisit(rec) {
  if (!rec) return
  const list = readRecent()
  if (same(list[0], rec)) return
  write(RECENT_KEY, withVisit(list, rec))
}

export function isPinned(rec, pinned = readPinned()) {
  return pinned.some(p => same(p, rec))
}

export function togglePin(rec) {
  const pinned = readPinned()
  write(PINNED_KEY, isPinned(rec, pinned) ? pinned.filter(p => !same(p, rec)) : [...pinned, { type: rec.type, id: rec.id }])
}

export function forget(rec) {
  write(RECENT_KEY, readRecent().filter(r => !same(r, rec)))
  write(PINNED_KEY, readPinned().filter(r => !same(r, rec)))
}

export function onRecentChanged(fn) {
  const onStorage = (e) => { if (e.key === RECENT_KEY || e.key === PINNED_KEY) fn() }
  window.addEventListener(CHANGED, fn)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(CHANGED, fn)
    window.removeEventListener('storage', onStorage)
  }
}

/** Recents for the sidebar: pinned ones are shown above, so they are left out here. */
export function recentWithoutPinned(recent, pinned, limit = 5) {
  return recent.filter(r => !pinned.some(p => same(p, r))).slice(0, limit)
}
